/// <reference types="vite-plugin-pwa/client" />
import { registerSW } from 'virtual:pwa-register';

/**
 * Keeping the installed app on the current build.
 *
 * The service worker is what lets the app open in a basement with no signal, so
 * it stays — but a cache that serves yesterday's bundle after a deploy is worse
 * than no cache at all. This makes an update land predictably:
 *
 *   - check for a new build at startup, on resume, and hourly while open
 *   - take the new service worker immediately rather than waiting for every tab
 *     to close, which on a phone can be days
 *   - reload only when it is safe to: on resume, or after a short settle, never
 *     in the middle of something the user is typing
 *
 * Reloading is safe here because nothing in this app lives only in the page —
 * confirmed values are written to Firestore, and anything captured offline is
 * already queued in IndexedDB.
 */

const CHECK_INTERVAL_MS = 60 * 60 * 1000;

let updateReady = false;
let reloading = false;

/** A reload while someone is mid-entry would throw away what they typed. */
function isBusy(): boolean {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    if (el.value.trim() !== '') return true;
  }
  if (el instanceof HTMLSelectElement) return true;
  // The confirmation screen is the one place where losing state costs real work.
  return document.querySelector('[data-confirming="true"]') !== null;
}

function applyUpdate() {
  if (!updateReady || reloading || isBusy()) return;
  reloading = true;
  window.location.reload();
}

export function startUpdateWatcher() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      updateReady = true;
      // Give the current interaction a moment to finish before swapping.
      window.setTimeout(applyUpdate, 1500);
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      window.setInterval(() => {
        // Only ask when online; a failed check just leaves the current build in place.
        if (navigator.onLine) void registration.update().catch(() => {});
      }, CHECK_INTERVAL_MS);
    },
  });

  // Returning to the app is the natural moment to pick up a new build — it is
  // also exactly when someone expects "I closed and reopened it" to have worked.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (updateReady) {
      applyUpdate();
      return;
    }
    if (navigator.onLine) void updateSW(true).catch(() => {});
  });

  window.addEventListener('online', () => {
    void updateSW(true).catch(() => {});
  });
}
